import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Copy,
  Check,
  Code,
  Play,
  ExternalLink,
  Info,
  Fingerprint,
  ChevronDown,
  Settings2,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Lock,
  ArrowRight,
  Zap,
  ClipboardList,
  ShieldCheck,
  Save,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useTranslation from '@/hooks/useTranslation';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useMutateProjectUpdate } from '@/reactQuery/post';
import { flattenGroupToQuestions } from '@/utils';
import {
  extractPlaceholderNames,
  substitutePlaceholders,
  PLATFORMS,
  TIER_COPY,
  type Platform,
  type PlatformTier,
} from '@/utils/placeholders';
import {
  buildEmbedQuery,
  buildNfieldBlock,
  HOST_PAGE_SCRIPT,
  QUALTRICS_QUESTION_JS,
  type EmbedQueryParam,
} from './embedSnippets';
import { buildIntegrationBrief } from './embedBrief';
import languagesList from '@/lang/languages/languagesList';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';
import { cn } from '@/lib/utils';
import { EProjectStatus } from '@/enums';

// M5 — SINGLE SOURCE OF TRUTH for the per-platform respondent-ID token is
// `PLATFORMS[].urlSyntax` in placeholders.ts (e.g. Decipher = `${uuid}`, the
// Forsta-documented piped-variable syntax). Derive the generator's defaults from
// it so the two maps can never diverge again (previously Decipher disagreed:
// generator `[%pid%]` vs config `${uuid}`).
const PLATFORM_RESPONDENT_ID_DEFAULTS: Record<Platform, string> = Object.fromEntries(
  PLATFORMS.map((p) => [p.id, p.urlSyntax || '{{RESPONDENT_ID}}']),
) as Record<Platform, string>;

// Placeholder values the generator seeds by default — used to detect when the
// researcher left the respondent-ID unreplaced. Derived from the same source.
const KNOWN_PLACEHOLDERS = new Set<string>([
  ...PLATFORMS.map((p) => p.urlSyntax).filter(Boolean),
  '{{RESPONDENT_ID}}',
  '{{ID}}',
]);

// #11 — only the GENERIC sentinels mean "unfilled". A platform's urlSyntax token
// (e.g. Decipher `${uuid}`, Qualtrics `${e://Field/ResponseID}`) IS the correct,
// runtime-interpolated value — warning on it fires a false alarm on the happy path.
const GENERIC_PLACEHOLDERS = new Set<string>(['{{RESPONDENT_ID}}', '{{ID}}']);

const LAST_PLATFORM_KEY = 'telletEmbedLastPlatform';

// Per-platform paste locations + host/Qualtrics scripts live in
// @/utils/placeholders (config) and ./embedSnippets (emitted artifacts) —
// single sources of truth since TELV1-487.

interface EmbedCodeGeneratorProps {
  projectLink: string;
  projectInputMethods?: string[];
  projectStatus?: EProjectStatus;
}

interface CustomParam {
  id: string;
  key: string;
  value: string;
}

const INPUT_METHOD_OPTIONS = [
  { id: 'text', label: 'Text' },
  { id: 'audio', label: 'Speech' },
  { id: 'video', label: 'Video' },
  { id: 'image', label: 'Image' },
];

const DIMENSION_PRESETS = [
  { name: 'compact', label: 'Compact', height: '600px', width: '100%' },
  { name: 'standard', label: 'Standard', height: '650px', width: '100%' },
  { name: 'tall', label: 'Tall', height: '800px', width: '100%' },
  { name: 'fullScreen', label: 'Full height', height: '100vh', width: '100%' },
];

function isProjectLive(status: EProjectStatus | undefined): boolean {
  return status === EProjectStatus.PUBLISHED || status === EProjectStatus.COMPLETED;
}

interface StepStripProps {
  projectStatus?: EProjectStatus;
}

function StepStrip({ projectStatus }: StepStripProps) {
  const live = isProjectLive(projectStatus);

  const steps = [
    { n: 1, label: 'Configure' },
    { n: 2, label: 'Copy snippet' },
    { n: 3, label: 'Preview' },
    { n: 4, label: 'Go live' },
  ];

  return (
    <div className="flex items-center gap-0 mb-5">
      {steps.map((step, idx) => {
        const isLastStep = step.n === 4;
        const isLiveStep = isLastStep && live;
        // Honest orientation: steps 1–3 show the flow (numbered, neutral), not a
        // completion state — don't fake green checkmarks before the researcher
        // has done anything. Only "Go live" lights up, and only when the project
        // is actually live.
        const isDone = isLiveStep;

        const circleClass = isDone ? 'bg-primary text-white' : 'bg-[#E3E8EF] text-[#94A3B8]';

        const textClass = isDone ? 'text-primary' : 'text-[#5A6A85]';

        const connectorColor = live ? '#BBE6CC' : '#E3E8EF';

        const stepContent: React.ReactNode = isLiveStep ? <Zap size={10} /> : step.n;

        return (
          <div key={step.n} className="flex items-center flex-1 min-w-0">
            <div className="flex items-center gap-[6px] shrink-0">
              <div
                className={cn(
                  'w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-semibold leading-none shrink-0',
                  circleClass,
                )}
              >
                {stepContent}
              </div>
              <span className={cn('text-[11.5px] font-medium whitespace-nowrap', textClass)}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className="flex-1 h-px mx-2 min-w-[8px]"
                style={{ background: connectorColor }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold text-light-gray uppercase tracking-wide mb-2">
      {children}
    </p>
  );
}

// TELV1-487 — honest support-tier badge, rendered in the platform dropdown
// (and mirrored into the trigger via SelectValue). Static span only — Radix
// ItemText must not contain interactive content.
const TIER_PILL_STYLES: Record<PlatformTier, string> = {
  verified: 'bg-primary/10 text-primary',
  'best-effort': 'bg-[#EEF2F6] text-[#5A6A85]',
  generic: 'border border-[#DCE2EA] text-[#94A3B8]',
};

function TierPill({ tier }: { tier: PlatformTier }) {
  return (
    <span
      className={cn(
        'text-[10px] font-semibold px-1.5 py-[2px] rounded-full leading-none shrink-0',
        TIER_PILL_STYLES[tier],
      )}
    >
      {TIER_COPY[tier].label}
    </span>
  );
}

function EmbedCodeGenerator({
  projectLink,
  projectInputMethods = [],
  projectStatus,
}: EmbedCodeGeneratorProps) {
  const { lang } = useTranslation();
  const { currentProject } = useCurrentProject();

  // Form state
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [skipConsent, setSkipConsent] = useState(false);
  const [inputMethods, setInputMethods] = useState<string[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Platform selector
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>(() => {
    if (typeof window === 'undefined') return 'decipher';
    const saved = window.localStorage?.getItem(LAST_PLATFORM_KEY);
    if (saved === 'forsta') return 'decipher';
    if (saved && PLATFORMS.some((p) => p.id === saved)) return saved as Platform;
    return 'decipher';
  });

  const [customParams, setCustomParams] = useState<CustomParam[]>([
    {
      id: crypto.randomUUID(),
      key: 'respondent_id',
      value: PLATFORM_RESPONDENT_ID_DEFAULTS[selectedPlatform] || '{{RESPONDENT_ID}}',
    },
  ]);
  const [width, setWidth] = useState('100%');
  // TELV1-531 — wave label for tracker studies; rides the URL as ?wave=.
  const [waveLabel, setWaveLabel] = useState('');
  const [height, setHeight] = useState('600px');
  const [copied, setCopied] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);
  // TELV1-487 — Qualtrics Part 2 (question JavaScript) has its own copy state.
  const [copiedQjs, setCopiedQjs] = useState(false);
  // TELV1-529 — "Copy integration brief" copy state.
  const [copiedBrief, setCopiedBrief] = useState(false);

  // Advanced embed size — custom size disclosure
  const [customSizeOpen, setCustomSizeOpen] = useState(false);
  // Host-page script disclosure — collapsed by default so the iframe snippet
  // (the primary deliverable) isn't buried under 40 lines of one-time setup code.
  const [scriptOpen, setScriptOpen] = useState(false);

  const { organization_id, workspace_id, project_id } = useParams();
  const { mutateAsync: mutateProjectUpdate } = useMutateProjectUpdate(
    organization_id,
    workspace_id,
    project_id,
  );
  // TELV1-531 — hydrate the generator from the persisted per-project config
  // once, when the project arrives. Explicit-save model (no auto-save): the
  // researcher edits freely and presses "Save configuration".
  const hydratedRef = useRef(false);
  useEffect(() => {
    const cfg = currentProject?.embed_generator_config;
    if (!cfg || hydratedRef.current) return;
    hydratedRef.current = true;
    if (cfg.platform) setSelectedPlatform(cfg.platform as Platform);
    if (Array.isArray(cfg.custom_params) && cfg.custom_params.length > 0) {
      setCustomParams(
        cfg.custom_params.map((cp, i) => ({
          id: `cfg-${i}`,
          key: cp.key,
          value: cp.value,
        })),
      );
    }
    if (cfg.width) setWidth(cfg.width);
    if (cfg.height) setHeight(cfg.height);
    if (cfg.language) setSelectedLanguage(cfg.language);
    if (typeof cfg.skip_consent === 'boolean') setSkipConsent(cfg.skip_consent);
    if (Array.isArray(cfg.input_methods) && cfg.input_methods.length > 0)
      setInputMethods(cfg.input_methods);
    if (cfg.wave_label) setWaveLabel(cfg.wave_label);
  }, [currentProject?.embed_generator_config]);

  const [savingConfig, setSavingConfig] = useState(false);
  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await mutateProjectUpdate({
        embed_generator_config: {
          platform: selectedPlatform,
          custom_params: customParams.map(({ key, value }) => ({ key, value })),
          width,
          height,
          language: selectedLanguage ?? null,
          skip_consent: skipConsent,
          input_methods: inputMethods,
          wave_label: waveLabel || null,
        },
      } as never);
      toast.success(lang.get('msg.embedConfigSaved'));
    } catch {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    } finally {
      setSavingConfig(false);
    }
  };

  const handlePlatformChange = useCallback(
    (platform: Platform) => {
      const previousDefault = PLATFORM_RESPONDENT_ID_DEFAULTS[selectedPlatform] || '';
      const nextDefault = PLATFORM_RESPONDENT_ID_DEFAULTS[platform] || '';
      setSelectedPlatform(platform);
      if (typeof window !== 'undefined') {
        window.localStorage?.setItem(LAST_PLATFORM_KEY, platform);
      }
      setCustomParams((prev) =>
        prev.map((p) =>
          p.key.trim().toLowerCase() === 'respondent_id' &&
          (p.value === previousDefault || p.value === '{{ID}}' || KNOWN_PLACEHOLDERS.has(p.value))
            ? { ...p, value: nextDefault }
            : p,
        ),
      );
    },
    [selectedPlatform],
  );

  const handleResetRespondentId = useCallback(() => {
    const defaultValue = PLATFORM_RESPONDENT_ID_DEFAULTS[selectedPlatform] || '{{RESPONDENT_ID}}';
    setCustomParams((prev) =>
      prev.map((p) =>
        p.key.trim().toLowerCase() === 'respondent_id' ? { ...p, value: defaultValue } : p,
      ),
    );
  }, [selectedPlatform]);

  // %%var%% placeholder inspection
  const detectedPlaceholders = useMemo(() => {
    if (!currentProject?.interview_questions) return [];
    const flatQuestions = flattenGroupToQuestions(currentProject.interview_questions);
    const questionTexts = flatQuestions.map((q) => q.question || '');
    return extractPlaceholderNames(currentProject.opening_message, ...questionTexts);
  }, [currentProject?.interview_questions, currentProject?.opening_message]);

  const customParamKeysLower = useMemo(
    () => new Set(customParams.map((p) => p.key.trim().toLowerCase()).filter((k) => k.length > 0)),
    [customParams],
  );

  const missingPlaceholders = useMemo(
    () => detectedPlaceholders.filter((name) => !customParamKeysLower.has(name)),
    [detectedPlaceholders, customParamKeysLower],
  );

  const unreferencedCustomKeys = useMemo(() => {
    const detected = new Set(detectedPlaceholders);
    const standard = new Set([
      'respondent_id',
      'participant_id',
      'language',
      'skipconsent',
      'source',
      'environment',
      'inputmethods',
    ]);
    return customParams
      .map((p) => p.key.trim())
      .filter((k) => {
        if (!k) return false;
        const lower = k.toLowerCase();
        return !detected.has(lower) && !standard.has(lower);
      });
  }, [customParams, detectedPlaceholders]);

  // Test piping preview
  const [showPipingPreview, setShowPipingPreview] = useState(false);
  const pipingPreview = useMemo(() => {
    if (!currentProject?.interview_questions) return null;
    const values: Record<string, string> = {};
    customParams.forEach((p) => {
      if (p.key.trim() && p.value.trim()) values[p.key.trim()] = p.value.trim();
    });
    const flatQuestions = flattenGroupToQuestions(currentProject.interview_questions);
    return {
      openingMessage: substitutePlaceholders(currentProject.opening_message || '', values),
      questions: flatQuestions.map((q) => ({
        id: q.id,
        original: q.question || '',
        substituted: substitutePlaceholders(q.question || '', values),
      })),
    };
  }, [currentProject, customParams]);

  const allowsLanguageOverride = !!currentProject?.allow_language_selection;
  const projectLanguageLabel = currentProject?.language || '';

  const isUnpublished =
    projectStatus !== undefined &&
    projectStatus !== EProjectStatus.PUBLISHED &&
    projectStatus !== EProjectStatus.COMPLETED;

  const validationError = useMemo(() => {
    if (isUnpublished) {
      return 'Unpublished — snippet will not load';
    }
    if (skipConsent && allowsLanguageOverride && !selectedLanguage) {
      return lang.get('msg.embedGeneratorLanguageRequired');
    }
    return null;
  }, [isUnpublished, skipConsent, allowsLanguageOverride, selectedLanguage, lang]);

  const missingProjectFields = useMemo(() => {
    if (!currentProject) return [] as string[];
    const missing: string[] = [];
    if (!currentProject.language) missing.push(lang.get('msg.primaryLanguage'));
    if (!currentProject.privacy_policy) missing.push(lang.get('msg.privacyPolicy'));
    if (!currentProject.interview_goal) missing.push(lang.get('msg.myResearchObjective'));
    if (!currentProject.interview_audience) missing.push(lang.get('msg.interviewAudience'));
    const questionCount = currentProject.interview_questions
      ? flattenGroupToQuestions(currentProject.interview_questions).length
      : 0;
    if (questionCount === 0) missing.push(lang.get('msg.atLeastOneQuestion'));
    return missing;
  }, [currentProject, lang]);

  const testButtonDisabled = !!validationError || missingProjectFields.length > 0;

  let testTooltipText = '';
  if (testButtonDisabled) {
    testTooltipText =
      missingProjectFields.length > 0
        ? `Complete required fields to test: ${missingProjectFields.join(', ')}`
        : validationError ?? '';
  }

  // Build the URL query as ordered entries (no environment param — that's
  // sandbox-only, driven by the test button). Generator-controlled params
  // first, researcher params after — buildEmbedQuery's last-wins dedupe then
  // preserves the old URLSearchParams.set override semantics.
  //
  // TELV1-487 — researcher values (respondent_id, %%var%% mappings, extra
  // params) are emitted RAW: platform pipe tokens must appear literally for
  // the host platform to substitute them, and the docs instruct researchers
  // to pre-encode literal values themselves.
  const queryEntries = useMemo(() => {
    const entries: EmbedQueryParam[] = [];
    if (allowsLanguageOverride) {
      if (selectedLanguage) {
        entries.push({ key: 'language', value: selectedLanguage, encodeValue: true });
      }
    } else if (currentProject?.language) {
      // #10 — fixed-language project: emit the project's language so
      // `skipConsent` fully skips the intro. The consent screen only
      // auto-skips when BOTH skipConsent AND a language are present; without
      // this a fixed-language embed still shows a "Let's go" interstitial.
      entries.push({ key: 'language', value: currentProject.language, encodeValue: true });
    }
    if (skipConsent) entries.push({ key: 'skipConsent', value: 'true' });
    if (inputMethods.length > 0)
      entries.push({ key: 'inputMethods', value: inputMethods.join(',') });
    // TELV1-531 — wave label tags every conversation for wave-over-wave
    // tracking (auto-appears in exports, transcripts, filters, profiles).
    if (waveLabel.trim()) entries.push({ key: 'wave', value: waveLabel.trim(), encodeValue: true });
    // respondent_id and other params always included
    customParams.forEach((param) => {
      entries.push({ key: param.key, value: param.value });
    });
    return entries;
  }, [
    selectedLanguage,
    allowsLanguageOverride,
    currentProject?.language,
    skipConsent,
    inputMethods,
    customParams,
    waveLabel,
  ]);

  const generatedUrl = useMemo(() => {
    const queryString = buildEmbedQuery(queryEntries);
    return queryString ? `${projectLink}?${queryString}` : projectLink;
  }, [projectLink, queryEntries]);

  // Sandbox test URL — same query but with environment=sandbox, routed through
  // /test/. Built from projectLink (clean base URL) rather than parsing
  // generatedUrl: raw pipe tokens can contain '#' (Dimensions `{#name}`),
  // which would truncate URL parsing at the fragment.
  const testPageUrl = useMemo(() => {
    try {
      const u = new URL(projectLink);
      const pathSegments = u.pathname.split('/').filter(Boolean);
      const projectIdSegment = pathSegments[pathSegments.length - 1] || '';
      // Appended last so it overrides any researcher-supplied environment.
      const sandboxQuery = buildEmbedQuery([
        ...queryEntries,
        { key: 'environment', value: 'sandbox' },
      ]);
      return `${u.origin}/test/${projectIdSegment}?${sandboxQuery}`;
    } catch {
      return generatedUrl;
    }
  }, [projectLink, queryEntries, generatedUrl]);

  const iframeCode = useMemo(() => {
    return `<iframe id="tellet-iframe"\n  src="${generatedUrl}"\n  allow="camera; microphone; autoplay;"\n  title="Tellet"\n  width="${width}" height="${height}"\n  frameBorder="0"></iframe>`;
  }, [generatedUrl, width, height]);

  const selectedConfig = PLATFORMS.find((p) => p.id === selectedPlatform) ?? PLATFORMS[0];
  const platformLabel = selectedConfig.label;
  const isQualtrics = selectedPlatform === 'qualtrics';
  const isNfield = selectedPlatform === 'nfield';
  // Qualtrics relocates the host script into the question's JS editor (Part 2)
  // and Nfield bundles it inside the << >> block (TELV1-488), so the generic
  // once-per-page script disclosure would be a duplicate on both.
  const showHostScriptDisclosure = !isQualtrics && !isNfield;

  // TELV1-488 — Nfield ships iframe + relay as ONE paste unit inside ODIN's
  // << >> script brackets (in-page; NOT the AITM redirect). Other platforms
  // keep the bare iframe as the primary snippet.
  const primarySnippet = useMemo(
    () => (isNfield ? buildNfieldBlock(iframeCode) : iframeCode),
    [isNfield, iframeCode],
  );

  const handleInputMethodChange = useCallback((methodId: string, checked: boolean) => {
    setInputMethods((prev) => (checked ? [...prev, methodId] : prev.filter((m) => m !== methodId)));
  }, []);

  const handleAddCustomParam = useCallback(() => {
    setCustomParams((prev) => [...prev, { id: crypto.randomUUID(), key: '', value: '' }]);
  }, []);

  const focusValueInputForKeyRef = useRef<string | null>(null);
  const handleAddMissingPlaceholder = useCallback((name: string) => {
    setCustomParams((prev) => {
      const existing = prev.find((p) => p.key.trim().toLowerCase() === name.toLowerCase());
      if (existing) {
        focusValueInputForKeyRef.current = existing.id;
        return prev;
      }
      const newRow = { id: crypto.randomUUID(), key: name, value: '' };
      focusValueInputForKeyRef.current = newRow.id;
      return [...prev, newRow];
    });
  }, []);

  useEffect(() => {
    const targetId = focusValueInputForKeyRef.current;
    if (!targetId) return;
    const el = document.querySelector<HTMLInputElement>(
      `[data-custom-param-value-id="${targetId}"]`,
    );
    if (el) {
      el.focus();
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    focusValueInputForKeyRef.current = null;
  }, [customParams]);

  const handleRemoveCustomParam = useCallback((index: number) => {
    setCustomParams((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCustomParamChange = useCallback(
    (index: number, field: 'key' | 'value', value: string) => {
      setCustomParams((prev) =>
        prev.map((param, i) => (i === index ? { ...param, [field]: value } : param)),
      );
    },
    [],
  );

  const handlePresetSelect = useCallback((presetName: string) => {
    const preset = DIMENSION_PRESETS.find((p) => p.name === presetName);
    if (preset) {
      setWidth(preset.width);
      setHeight(preset.height);
      setCustomSizeOpen(false);
    }
  }, []);

  // TELV1-487 — one guarded copy path for every snippet block. The validation
  // guard applies to all of them; the respondent-id placeholder warning only
  // to blocks that carry the embed URL (iframe / Nfield block), not to
  // URL-less script parts.
  const copySnippet = useCallback(
    async (
      code: string,
      setCopiedFlag: (v: boolean) => void,
      opts: { warnRespondentId?: boolean } = {},
    ) => {
      if (validationError) {
        toast.error(validationError);
        return;
      }
      if (opts.warnRespondentId) {
        const respondentParam = customParams.find(
          (p) => p.key.trim().toLowerCase() === 'respondent_id',
        );
        if (respondentParam && GENERIC_PLACEHOLDERS.has(respondentParam.value)) {
          toast.warn(
            `It looks like respondent_id still contains a placeholder. Replace it with your survey platform’s respondent ID variable, or all responses will share the same ID.`,
          );
        }
      }
      try {
        await navigator.clipboard.writeText(code);
        toast.success(lang.get('msg.codeCopied'));
        setCopiedFlag(true);
        setTimeout(() => setCopiedFlag(false), 2000);
      } catch {
        toast.error(lang.get('msg.codeNotCopied'));
      }
    },
    [lang, validationError, customParams],
  );

  const handleCopyCode = useCallback(
    () => copySnippet(primarySnippet, setCopied, { warnRespondentId: true }),
    [copySnippet, primarySnippet],
  );

  const handleCopyQualtricsJs = useCallback(
    () => copySnippet(QUALTRICS_QUESTION_JS, setCopiedQjs),
    [copySnippet],
  );

  const handleCopyScript = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(HOST_PAGE_SCRIPT);
      toast.success(lang.get('msg.codeCopied'));
      setCopiedScript(true);
      setTimeout(() => setCopiedScript(false), 2000);
    } catch {
      toast.error(lang.get('msg.codeNotCopied'));
    }
  }, [lang]);

  const projectMethodsHint =
    projectInputMethods.length > 0
      ? projectInputMethods
          .map((m) => {
            if (m === 'audio') return 'Speech';
            return m.charAt(0).toUpperCase() + m.slice(1);
          })
          .join(', ')
      : 'Text, Speech, Video';

  // Split customParams: respondent_id card vs placeholder rows vs advanced "extra" params
  const respondentIdParam = customParams.find(
    (p) => p.key.trim().toLowerCase() === 'respondent_id',
  );
  const respondentIdIndex = customParams.findIndex(
    (p) => p.key.trim().toLowerCase() === 'respondent_id',
  );

  // Extra params: not respondent_id and not a detected placeholder
  const detectedPlaceholderSet = useMemo(
    () => new Set(detectedPlaceholders.map((n) => n.toLowerCase())),
    [detectedPlaceholders],
  );
  const extraParams = customParams.filter((p) => {
    const lower = p.key.trim().toLowerCase();
    return lower !== 'respondent_id' && !detectedPlaceholderSet.has(lower);
  });

  // Active preset for embed size select
  const activePresetName = useMemo(() => {
    const match = DIMENSION_PRESETS.find((p) => p.width === width && p.height === height);
    return match ? match.name : 'custom';
  }, [width, height]);

  // True when the respondent-id field still holds the platform's pre-filled
  // variable (the correct value). Drives the "leave as is" confirmation and
  // hides Reset until the researcher actually changes it.
  const respondentIdIsDefault =
    (respondentIdParam?.value ?? '') ===
    (PLATFORM_RESPONDENT_ID_DEFAULTS[selectedPlatform] || '{{RESPONDENT_ID}}');

  // TELV1-529 — one clipboard write with everything a survey programmer
  // needs. Built at click time from the SAME derived state the tab renders
  // (single source — the brief can never disagree with the screen), and
  // routed through copySnippet so the unpublished guard + toasts apply.
  const handleCopyBrief = () => {
    const brief = buildIntegrationBrief({
      projectTitle: currentProject?.title ?? '',
      projectId: currentProject?._id ?? '',
      dashboardUrl: window.location.href,
      platformLabel,
      tierLabel: TIER_COPY[selectedConfig.tier].label,
      tierDescription: TIER_COPY[selectedConfig.tier].description,
      pasteHint: selectedConfig.pasteHint,
      published: !isUnpublished,
      primarySnippet,
      secondarySnippet: isQualtrics ? QUALTRICS_QUESTION_JS : '',
      hostScript: showHostScriptDisclosure ? HOST_PAGE_SCRIPT : '',
      respondentIdValue: respondentIdParam?.value ?? '',
      placeholders: detectedPlaceholders.map((name) => {
        const param = customParams.find((p) => p.key.trim().toLowerCase() === name.toLowerCase());
        return {
          name,
          value: param?.value ?? '',
          example: selectedConfig.pipeExample ? selectedConfig.pipeExample(name) : '',
        };
      }),
      testUrl: testPageUrl,
      liveUrl: generatedUrl,
      docsUrl: `${window.location.origin}/documentation/publishing-sharing/survey-embedding.md#postmessage-api`,
      language: selectedLanguage ?? currentProject?.language ?? 'English',
      waveLabel: waveLabel || '',
      consentShown: !skipConsent,
      inputMethods,
      width,
      height,
    });
    copySnippet(brief, setCopiedBrief);
  };

  return (
    <div className="flex flex-col w-full">
      {/* Header */}
      <span className="mb-2 inline-flex w-fit items-center gap-1 rounded-full bg-embed-bg px-2 py-0.5 text-[10.5px] font-semibold text-embed">
        <Code size={12} />
        Tellet Embed
      </span>
      <p className="text-[15px] font-semibold text-dark-text mb-1">Embed your study in a survey</p>
      <p className="text-[12.5px] text-[#5A6A85] mb-5">
        Paste this snippet into your survey platform. Tellet runs the conversation inside your
        survey and sends each respondent&apos;s answers and summary back to their row in your
        results.
      </p>

      {/* Step strip */}
      <StepStrip projectStatus={projectStatus} />

      {/* ─── 1 · Survey platform ─── */}
      <SectionLabel>1 · Survey platform</SectionLabel>
      <Select value={selectedPlatform} onValueChange={(v) => handlePlatformChange(v as Platform)}>
        <SelectTrigger className="text-sm w-full mb-1">
          <SelectValue placeholder="Select your survey platform" />
        </SelectTrigger>
        <SelectContent>
          {PLATFORMS.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              <span className="flex items-center gap-2">
                {p.label}
                <TierPill tier={p.tier} />
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* TELV1-487 — honest support copy per tier (replaces the false blanket
          "works with any survey platform" claim; SurveyMonkey, for one,
          strips iframes and scripts on every plan). */}
      <p className="text-[12px] text-[#5A6A85] mb-0">
        {TIER_COPY[selectedConfig.tier].description} See the paste location for {platformLabel}{' '}
        below.
      </p>
      {selectedPlatform !== 'vanilla' && (
        <p className="text-[11px] text-[#94A3B8] mt-1 mb-0">
          Platform not listed? Choose &ldquo;Vanilla JS (other / custom)&rdquo; — any page that
          allows custom HTML and JavaScript works.
        </p>
      )}

      {/* ─── 2 · Respondent ID mapping ─── */}
      <SectionLabel>
        <span className="flex items-center gap-1 mt-[18px]">2 · Respondent ID mapping</span>
      </SectionLabel>
      <div className="bg-white border rounded-lg p-[14px] mb-0">
        <div className="flex items-center gap-1.5 mb-2">
          <Fingerprint size={13} className="text-light-gray shrink-0" />
          <p className="text-[12.5px] font-semibold text-dark-text">
            Tag each conversation with your survey&apos;s respondent ID
          </p>
        </div>
        <p className="text-[11.5px] text-[#5A6A85] mb-[10px]">
          Tellet writes this ID back with every response, so you can match each answer to the right
          person in your survey results. It&apos;s pre-filled with {platformLabel}&apos;s respondent
          ID variable.
        </p>
        <div className="flex gap-2 items-center">
          <span className="text-[12px] bg-secondary-background border rounded-md px-[11px] py-2 text-light-gray font-mono shrink-0">
            respondent_id
          </span>
          <span className="text-light-gray text-sm">=</span>
          <Input
            className="flex-1 text-[12px] font-mono"
            value={respondentIdParam?.value ?? ''}
            onChange={(e) => {
              if (respondentIdIndex >= 0) {
                handleCustomParamChange(respondentIdIndex, 'value', e.target.value);
              }
            }}
            data-custom-param-value-id={respondentIdParam?.id}
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          {respondentIdIsDefault ? (
            <p className="flex items-center gap-1 text-[11px] text-primary">
              <CheckCircle2 size={13} className="shrink-0" />
              Pre-filled for {platformLabel} — leave as is
            </p>
          ) : (
            <>
              <p className="text-[11px] text-[#5A6A85]">Custom value</p>
              <button
                type="button"
                onClick={handleResetRespondentId}
                className="text-[11px] text-primary hover:underline"
              >
                Reset to {platformLabel} default
              </button>
            </>
          )}
        </div>
      </div>

      {/* ─── Question variables (shown only when placeholders detected) ─── */}
      {detectedPlaceholders.length > 0 && (
        <div
          className="mt-[18px] rounded-[10px] p-[14px] border"
          style={{ background: '#FEF3D9', borderColor: '#F0E2C0' }}
        >
          <p
            className="text-[13px] font-semibold mb-1 flex items-center gap-1.5"
            style={{ color: '#854F0B' }}
          >
            <AlertTriangle size={13} className="shrink-0" />
            Map your question variables &mdash; action needed
          </p>
          <p className="text-[11.5px] mb-3" style={{ color: '#854F0B' }}>
            Your questions use placeholders. Give each one a value piped from your survey, or it
            will appear as literal text (e.g. &ldquo;%%brand%%&rdquo;) to the respondent.
          </p>
          <div className="flex flex-col gap-2">
            {detectedPlaceholders.map((name) => {
              const paramEntry = customParams.find(
                (p) => p.key.trim().toLowerCase() === name.toLowerCase(),
              );
              const paramIndex = customParams.findIndex(
                (p) => p.key.trim().toLowerCase() === name.toLowerCase(),
              );
              const hasValue = !!(paramEntry && paramEntry.value.trim());

              return (
                <div key={name} className="flex items-center gap-2">
                  <span
                    className="text-[12px] font-mono px-[9px] py-[5px] rounded-md border shrink-0"
                    style={{
                      background: '#FEF3D9',
                      borderColor: '#F0E2C0',
                      color: '#854F0B',
                    }}
                  >
                    %%{name}%%
                  </span>
                  <span className="text-[11px] shrink-0" style={{ color: '#B98A2E' }}>
                    =
                  </span>
                  <Input
                    className="flex-1 text-[12px] font-mono"
                    style={hasValue ? undefined : { borderColor: '#F0C060' }}
                    // TELV1-487 — platform-native pipe syntax (was hardcoded
                    // Qualtrics `${e://Field/...}` even on Decipher). Neutral
                    // fallback when the platform's syntax isn't verified.
                    placeholder={
                      selectedConfig.pipeExample
                        ? `e.g. ${selectedConfig.pipeExample(name)}`
                        : 'value piped from your survey'
                    }
                    value={paramEntry?.value ?? ''}
                    data-custom-param-value-id={paramEntry?.id}
                    onChange={(e) => {
                      if (paramIndex >= 0) {
                        handleCustomParamChange(paramIndex, 'value', e.target.value);
                      } else {
                        handleAddMissingPlaceholder(name);
                        // The newly added row gets focused via the effect; user can type after
                      }
                    }}
                  />
                  {hasValue ? (
                    <CheckCircle2 size={15} className="shrink-0 text-emerald-600" />
                  ) : (
                    <div className="w-[15px] shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[10.5px] mt-3" style={{ color: '#B98A2E' }}>
            {detectedPlaceholders.length - missingPlaceholders.length} of{' '}
            {detectedPlaceholders.length} mapped &middot; the snippet updates as you fill these in
          </p>
        </div>
      )}

      {/* TELV1-531 — optional wave label for tracker studies.
          TELV1-533 A9 — ALWAYS rendered: this used to live inside the
          %%placeholder%% mapping card, so placeholder-free trackers had no
          way to set it — and a duplicated wave-2 project silently kept
          wave=W1 with no visible control. It is also an optional feature,
          not an "action needed" warning, so it gets its own calm block. */}
      <div className="bg-white border rounded-lg p-[14px] mt-[14px]">
        <p className="text-[12.5px] font-semibold text-dark-text mb-1">Wave label</p>
        <p className="text-[11.5px] text-[#5A6A85] mb-[8px]">
          Optional — tags every conversation with wave=&lt;label&gt; so waves can be compared,
          filtered and exported side by side. Duplicated projects carry the previous wave&apos;s
          label: update it here for each new wave.
        </p>
        <Input
          value={waveLabel}
          onChange={(e) => setWaveLabel(e.target.value)}
          placeholder="e.g. W2 or 2026-Q3"
          className="h-8 w-56 text-[12.5px]"
        />
      </div>

      {/* ─── 3 · Respondent intro (TELV1-530) ─── */}
      <SectionLabel>3 · Respondent intro</SectionLabel>
      <div className="bg-white border rounded-lg p-[14px] mb-0">
        <div className="flex items-center gap-1.5 mb-2">
          <ShieldCheck size={15} className="text-primary shrink-0" />
          <p className="text-[12.5px] font-semibold text-dark-text">
            {lang.get('msg.embedGeneratorConsent')}
          </p>
        </div>
        <p className="text-[11.5px] text-[#5A6A85] mb-[10px]">
          {lang.get('msg.embedGeneratorConsentExplainer')}
        </p>
        <RadioGroup
          value={skipConsent ? 'skip' : 'show'}
          onValueChange={(v: string) => setSkipConsent(v === 'skip')}
          className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4"
        >
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem value="show" id="consent-show" />
            <span className="text-[12.5px]">{lang.get('msg.embedGeneratorShowConsent')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem value="skip" id="consent-skip" />
            <span className="text-[12.5px]">{lang.get('msg.embedGeneratorSkipConsent')}</span>
          </label>
        </RadioGroup>
        <p className="flex items-center gap-1 text-[11px] text-primary mt-2">
          <CheckCircle2 size={13} className="shrink-0" />
          {lang.get('msg.embedGeneratorConsentRecommendation')}
        </p>
        {skipConsent && (
          <p className="text-[11px] text-amber-600 mt-1">
            {lang.get('msg.embedGeneratorSkipConsentHint')}
          </p>
        )}
        {validationError && !isUnpublished && (
          <p className="text-[11px] text-destructive mt-1">{validationError}</p>
        )}
      </div>

      {/* ─── 4 · Embed snippet ─── */}
      <p className="text-[11px] font-semibold text-light-gray uppercase tracking-wide mb-2 mt-[18px]">
        4 · Embed snippet
      </p>

      {validationError && <p className="text-[11px] text-destructive mb-2">{validationError}</p>}

      {/* TELV1-487 — Qualtrics strips <script> from question HTML, so it gets
          a genuine two-part snippet: iframe in the question's HTML View, the
          relay script separately in Question behavior → JavaScript. All other
          platforms keep the single combined block. */}
      {isQualtrics && (
        <p className="text-[11.5px] font-semibold text-dark-text mb-1.5">
          Part 1 · iframe — paste into the question&apos;s HTML View
        </p>
      )}
      {isNfield && (
        <p className="text-[11.5px] font-semibold text-dark-text mb-1.5">
          ODIN question text — paste this whole block (keep the &lt;&lt; &gt;&gt; brackets)
        </p>
      )}

      {/* Dark snippet hero */}
      <div className="rounded-lg p-[14px] relative" style={{ background: '#102A22' }}>
        <button
          type="button"
          onClick={handleCopyCode}
          className="absolute top-2 right-2 bg-primary text-white text-[11px] font-medium px-[11px] py-[5px] rounded-md inline-flex gap-[5px] items-center hover:bg-primary/90 transition-colors"
          aria-label={copied ? 'Embed code copied to clipboard' : 'Copy embed code to clipboard'}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? 'Copied!' : 'Copy snippet'}
        </button>
        <pre
          className="font-mono text-[11.5px] leading-[1.7] m-0 whitespace-pre-wrap break-all pr-24"
          style={{ color: '#CFE9DD' }}
        >
          {primarySnippet}
        </pre>
        <span role="status" aria-live="polite" className="sr-only">
          {copied ? 'Copied to clipboard' : ''}
        </span>
      </div>

      {isQualtrics && (
        <>
          <p className="text-[11.5px] font-semibold text-dark-text mt-3 mb-1.5">
            Part 2 · Script — Question behavior → JavaScript (replace the editor&apos;s entire
            contents)
          </p>
          <div className="rounded-lg p-[14px] relative" style={{ background: '#102A22' }}>
            <button
              type="button"
              onClick={handleCopyQualtricsJs}
              className="absolute top-2 right-2 bg-primary text-white text-[11px] font-medium px-[11px] py-[5px] rounded-md inline-flex gap-[5px] items-center hover:bg-primary/90 transition-colors"
              aria-label={
                copiedQjs
                  ? 'Question JavaScript copied to clipboard'
                  : 'Copy question JavaScript to clipboard'
              }
            >
              {copiedQjs ? <Check size={12} /> : <Copy size={12} />}
              {copiedQjs ? 'Copied!' : 'Copy script'}
            </button>
            <pre
              className="font-mono text-[11.5px] leading-[1.7] m-0 whitespace-pre-wrap break-all pr-24"
              style={{ color: '#CFE9DD' }}
            >
              {QUALTRICS_QUESTION_JS}
            </pre>
            <span role="status" aria-live="polite" className="sr-only">
              {copiedQjs ? 'Copied to clipboard' : ''}
            </span>
          </div>
          <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
            <AlertTriangle size={11} className="shrink-0" />
            Custom JavaScript is a licensed Qualtrics feature — if you don&apos;t see the JavaScript
            editor, your license doesn&apos;t include it.
          </p>
        </>
      )}

      <p className="text-[11px] text-[#5A6A85] mt-1 mb-[14px] flex items-center gap-1">
        <Info size={11} className="shrink-0" />
        {selectedConfig.pasteHint}
      </p>

      {/* Host page script — collapsed by default (H5). Most researchers add it
          once to their survey page and never need to read it, so it shouldn't
          compete with the iframe snippet above for attention. Hidden on
          platforms whose snippet already carries the relay (Qualtrics Part 2). */}
      {showHostScriptDisclosure && (
        <button
          type="button"
          onClick={() => setScriptOpen((o) => !o)}
          aria-expanded={scriptOpen}
          className="flex items-center gap-1.5 text-[12px] font-medium text-dark-text mb-2 hover:text-primary transition-colors"
        >
          <ChevronDown
            size={14}
            className={cn('shrink-0 transition-transform', scriptOpen && 'rotate-180')}
          />
          Add this script once to your survey page
          <span className="text-[11px] font-normal text-[#5A6A85]">— one-time setup</span>
        </button>
      )}
      {showHostScriptDisclosure && scriptOpen && (
        <>
          <div className="rounded-lg p-[14px] relative" style={{ background: '#102A22' }}>
            <button
              type="button"
              onClick={handleCopyScript}
              className="absolute top-2 right-2 bg-primary text-white text-[11px] font-medium px-[11px] py-[5px] rounded-md inline-flex gap-[5px] items-center hover:bg-primary/90 transition-colors"
              aria-label={
                copiedScript ? 'Page script copied to clipboard' : 'Copy page script to clipboard'
              }
            >
              {copiedScript ? <Check size={12} /> : <Copy size={12} />}
              {copiedScript ? 'Copied!' : 'Copy script'}
            </button>
            <pre
              className="font-mono text-[11.5px] leading-[1.7] m-0 whitespace-pre-wrap break-all pr-24"
              style={{ color: '#CFE9DD' }}
            >
              {HOST_PAGE_SCRIPT}
            </pre>
            <span role="status" aria-live="polite" className="sr-only">
              {copiedScript ? 'Copied to clipboard' : ''}
            </span>
          </div>
          <p className="text-[11px] text-[#5A6A85] mt-1 flex items-center gap-1">
            <Info size={11} className="shrink-0" />
            Add once even with multiple embeds. It resumes the interview if the page reloads and
            lets your survey continue when it finishes.
          </p>
        </>
      )}
      <div className="mb-[18px]" />

      {/* ─── 5 · Test it ─── */}
      <SectionLabel>5 · Test it</SectionLabel>
      <div className="bg-white border rounded-lg p-[14px] mb-[18px]">
        {/* Amber preview-mode banner */}
        <div
          className="text-[11.5px] px-[11px] py-2 rounded-md border mb-3 flex items-center gap-[7px]"
          style={{ background: '#FEF3D9', color: '#854F0B', borderColor: '#F0E2C0' }}
          role="status"
        >
          <Info size={13} className="shrink-0" />
          Preview mode &mdash; isolated test. Won&apos;t appear in your results or count toward your
          respondent cap.
        </div>
        <div className="flex gap-[10px] items-center flex-wrap">
          <TooltipWrapper text={testTooltipText}>
            <button
              type="button"
              onClick={() => {
                if (!testButtonDisabled) {
                  window.open(testPageUrl, '_blank', 'noopener,noreferrer');
                }
              }}
              disabled={testButtonDisabled}
              className={cn(
                'bg-primary text-white text-[12.5px] font-medium px-[15px] py-2 rounded-md inline-flex gap-[6px] items-center transition-colors',
                testButtonDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/90',
              )}
            >
              <Play size={13} />
              Preview safely
            </button>
          </TooltipWrapper>
          <button
            type="button"
            onClick={() => window.open(generatedUrl, '_blank', 'noopener,noreferrer')}
            className="border border-[#DCE2EA] text-[#5A6A85] text-[12.5px] font-medium px-[15px] py-2 rounded-md inline-flex gap-[6px] items-center hover:bg-[#F7F8FB] transition-colors"
          >
            <ExternalLink size={13} />
            Open live link
          </button>
          {/* TELV1-529 — the researcher→programmer handoff in one click:
              snippet(s), paste location, respondent-ID note, variable
              mapping table, test link and event docs as paste-ready
              Markdown for email/Slack/tickets. */}
          <button
            type="button"
            onClick={handleCopyBrief}
            className="border border-[#DCE2EA] text-[#5A6A85] text-[12.5px] font-medium px-[15px] py-2 rounded-md inline-flex gap-[6px] items-center hover:bg-[#F7F8FB] transition-colors"
          >
            {copiedBrief ? <Check size={13} /> : <ClipboardList size={13} />}
            Copy integration brief
          </button>
          {/* TELV1-531 — persist the generator setup on the project so it
              survives reloads and rides along on Duplicate for next wave. */}
          <button
            type="button"
            onClick={handleSaveConfig}
            disabled={savingConfig}
            className="border border-[#DCE2EA] text-[#5A6A85] text-[12.5px] font-medium px-[15px] py-2 rounded-md inline-flex gap-[6px] items-center hover:bg-[#F7F8FB] transition-colors disabled:opacity-50"
          >
            <Save size={13} />
            {savingConfig ? 'Saving…' : 'Save configuration'}
          </button>
        </div>
        <p className="text-[11px] text-[#5A6A85] mt-2.5">
          Open live link opens the real embed — that conversation counts toward your results.
        </p>
      </div>

      {/* ─── Advanced disclosure ─── */}
      <button
        type="button"
        onClick={() => setAdvancedOpen((v) => !v)}
        className="w-full bg-white border rounded-lg px-[14px] py-[13px] flex items-center justify-between text-[13px] text-light-gray hover:bg-secondary-background/50 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Settings2 size={14} />
          Advanced
          <span className="text-[11px] text-[#94A3B8]">
            — consent, language, size, extra parameters
          </span>
        </span>
        <ChevronDown
          size={14}
          className={cn('transition-transform', advancedOpen ? 'rotate-180' : '')}
        />
      </button>

      {advancedOpen && (
        <div className="bg-white border border-t-0 rounded-b-lg p-[14px] flex flex-col gap-5">
          {/* ── RESPONDENT EXPERIENCE group ── */}
          <SectionLabel>Respondent experience</SectionLabel>
          <div className="bg-white border rounded-lg p-[14px] flex flex-col gap-5">
            {/* TELV1-530 — the consent choice moved OUT of Advanced into
                section 3 (Respondent intro): it is respondent-facing config,
                and burying it here was the discoverability defect. */}
            {/* Language */}
            <div className="flex flex-col gap-2">
              <label className="text-[12.5px] font-semibold text-dark-text">
                {lang.get('msg.embedGeneratorLanguage')}
              </label>
              {allowsLanguageOverride ? (
                <>
                  <Select
                    value={selectedLanguage ?? '__any__'}
                    onValueChange={(v) => setSelectedLanguage(v === '__any__' ? null : v)}
                  >
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder={lang.get('msg.embedGeneratorSelectLanguage')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">
                        {lang.get('msg.embedGeneratorSelectLanguage')}
                      </SelectItem>
                      {(
                        languagesList.language as { id: number; label: string; value: string }[]
                      ).map((lang_item) => (
                        <SelectItem key={lang_item.id} value={lang_item.label}>
                          {lang_item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-light-gray">
                    {lang.get('msg.embedGeneratorLanguageHint')}
                  </p>
                </>
              ) : (
                <>
                  <div className="px-3 py-2 text-[12.5px] rounded-md border bg-white flex items-center gap-2">
                    <Lock size={13} className="text-light-gray shrink-0" />
                    <span className="text-dark-text">
                      {projectLanguageLabel ||
                        lang.get('msg.embedGeneratorLanguageFixedFallback') ||
                        'Set on the project'}
                    </span>
                    <button
                      type="button"
                      className="ml-auto text-[11px] text-primary hover:underline flex items-center gap-0.5 shrink-0"
                    >
                      Change in Research Design
                      <ArrowRight size={11} className="ml-0.5" />
                    </button>
                  </div>
                  <p className="text-[11px] text-light-gray">
                    Fixed by the project &mdash; every respondent uses {projectLanguageLabel || '—'}
                    . Allow per-embed choice by turning on &apos;Let participants choose the
                    language&apos; in Research Design.
                  </p>
                </>
              )}
            </div>

            {/* Response types */}
            <div className="flex flex-col gap-2">
              <label className="text-[12.5px] font-semibold text-dark-text">
                Override response types
              </label>
              <div className="flex flex-wrap gap-4">
                {INPUT_METHOD_OPTIONS.map((method) => (
                  <label key={method.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={inputMethods.includes(method.id)}
                      onCheckedChange={(checked) =>
                        handleInputMethodChange(method.id, checked as boolean)
                      }
                    />
                    <span className="text-[12.5px]">{method.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-light-gray">
                Leave unchecked to use the project default ({projectMethodsHint}).
              </p>
            </div>
          </div>

          {/* ── EMBED & DATA group ── */}
          <SectionLabel>Embed &amp; data</SectionLabel>
          <div className="bg-white border rounded-lg p-[14px] flex flex-col gap-5">
            {/* Embed size */}
            <div className="flex flex-col gap-2">
              <label className="text-[12.5px] font-semibold text-dark-text">
                {lang.get('msg.embedGeneratorDimensions')}
              </label>
              <p className="text-[11px] text-light-gray">
                {lang.get('msg.embedGeneratorDimensionsHint') ||
                  'Pick a height that gives respondents room to record voice or video answers.'}
              </p>
              <Select
                value={activePresetName === 'custom' ? 'custom' : activePresetName}
                onValueChange={(v) => {
                  if (v !== 'custom') {
                    handlePresetSelect(v);
                  } else {
                    setCustomSizeOpen(true);
                  }
                }}
              >
                <SelectTrigger className="text-[12.5px]">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {DIMENSION_PRESETS.map((preset) => (
                    <SelectItem key={preset.name} value={preset.name}>
                      <span>{preset.label}</span>
                      <span className="ml-2 text-[11px] text-light-gray">
                        &middot; {preset.height}
                      </span>
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom size</SelectItem>
                </SelectContent>
              </Select>
              {(customSizeOpen || activePresetName === 'custom') && (
                <div className="flex items-center gap-4 mt-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-light-gray">
                      {lang.get('msg.embedGeneratorWidth')}:
                    </span>
                    <Input
                      value={width}
                      onChange={(e) => setWidth(e.target.value)}
                      className="w-24 text-[12.5px]"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-light-gray">
                      {lang.get('msg.embedGeneratorHeight')}:
                    </span>
                    <Input
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      className="w-24 text-[12.5px]"
                    />
                  </div>
                </div>
              )}
              {!customSizeOpen && activePresetName !== 'custom' && (
                <button
                  type="button"
                  className="text-[11px] text-primary hover:underline w-fit"
                  onClick={() => setCustomSizeOpen(true)}
                >
                  Custom size
                </button>
              )}
            </div>

            {/* Extra URL parameters */}
            <div className="flex flex-col gap-2">
              <label className="text-[12.5px] font-semibold text-dark-text">
                Extra URL parameters
              </label>
              <p className="text-[11px] text-light-gray">
                Pass additional values from your survey into the conversation (beyond the respondent
                ID and question variables above).
              </p>
              <div className="flex flex-col gap-2">
                {extraParams.map((param) => {
                  const globalIndex = customParams.findIndex((p) => p.id === param.id);
                  return (
                    <div key={param.id} className="flex items-center gap-2">
                      <Input
                        placeholder="Key"
                        value={param.key}
                        onChange={(e) =>
                          handleCustomParamChange(globalIndex, 'key', e.target.value)
                        }
                        className="flex-1 text-[12.5px]"
                      />
                      <Input
                        placeholder="Value"
                        value={param.value}
                        onChange={(e) =>
                          handleCustomParamChange(globalIndex, 'value', e.target.value)
                        }
                        className="flex-1 text-[12.5px]"
                        data-custom-param-value-id={param.id}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveCustomParam(globalIndex)}
                        className="shrink-0"
                        type="button"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddCustomParam}
                  className="w-fit text-[12.5px]"
                  type="button"
                >
                  <Plus size={16} className="mr-1" />
                  {lang.get('msg.embedGeneratorAddParam')}
                </Button>
              </div>

              {/* Unreferenced custom keys warning */}
              {unreferencedCustomKeys.length > 0 && (
                <div className="text-[11px] text-amber-700 mt-1">
                  {lang.get('msg.embedVarUnreferenced') ||
                    'These URL parameters aren’t used by any question (possibly a typo):'}{' '}
                  {unreferencedCustomKeys.map((k) => (
                    <code
                      key={k}
                      className="px-1 py-0.5 mx-0.5 bg-secondary-background border rounded text-[10.5px]"
                    >
                      {k}
                    </code>
                  ))}
                </div>
              )}

              {/* Piping preview toggle (kept for power users) */}
              {detectedPlaceholders.length > 0 && (
                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="text-[11px] text-light-gray">
                    {missingPlaceholders.length === 0
                      ? lang.get('msg.embedVarsAllProvided') ||
                        'All expected variables have values.'
                      : `${missingPlaceholders.length} ${lang.get('msg.embedVarsMissingCount') || 'missing'}`}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPipingPreview((v) => !v)}
                    className="text-[11px]"
                  >
                    {showPipingPreview
                      ? lang.get('msg.embedHidePiping') || 'Hide preview'
                      : lang.get('msg.embedTestPiping') || 'Preview piped questions'}
                  </Button>
                </div>
              )}
              {showPipingPreview && pipingPreview && (
                <div className="flex flex-col gap-2 mt-2 p-2 bg-secondary-background border border-border rounded text-[11px]">
                  {pipingPreview.openingMessage && (
                    <div>
                      <div className="font-semibold text-light-gray mb-1">Opening message</div>
                      <div className="whitespace-pre-wrap text-dark-text">
                        {pipingPreview.openingMessage}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-light-gray mb-1">Questions</div>
                    <ul className="flex flex-col gap-1">
                      {pipingPreview.questions.map((q, idx) => (
                        <li key={q.id} className="text-dark-text">
                          <span className="text-light-gray">{idx + 1}.</span>{' '}
                          {q.substituted || <span className="text-light-gray">(empty)</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmbedCodeGenerator;
